import { rgb, type PDFFont, type RGB } from "pdf-lib";
import { format } from "date-fns";
import type { BrandPayoutHistory } from "../../../services/payout.service";
import { formatCurrency } from "../../../utils/formatters";
import { isGeneratedAvatar, personInitials } from "../../../utils/brandLogo";
import {
  A4,
  createDocument,
  drawCircularImage,
  drawText,
  embedImageFromUrl,
} from "../../../utils/pdf";

/*
 * The brand payout invoice as a vector PDF. It mirrors the on-screen receipt
 * card fact for fact but is drawn directly, so there is no screenshot step
 * and nothing can drift out of place or blur.
 */

export const formatInvoiceDate = (value: string | null | undefined) =>
  value ? format(new Date(value), "MMMM d, yyyy") : "—";

/** "accountEmail" → "Account email" */
export const fieldLabel = (key: string) => {
  const spaced = key.replace(/([A-Z])/g, " $1").replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

export interface InvoiceMethod {
  name: string;
  fields: Record<string, string>;
}

/**
 * The settled method (paid) or the methods on file the brand can pay with
 * (pending): one entry per method, each with its field values.
 */
export function paymentMethodsOf(payout: BrandPayoutHistory): InvoiceMethod[] {
  if (payout.paymentDetails?.paymentMethodName) {
    return [
      {
        name: payout.paymentDetails.paymentMethodName,
        fields: payout.paymentDetails.fieldData ?? {},
      },
    ];
  }
  return (payout.matchingPaymentInfos ?? []).map((info) => ({
    name: info.paymentMethod.name,
    fields: info.fieldData ?? {},
  }));
}

/** "124 58 237" (a brand token triplet) → pdf-lib colour; falls back to Reveshare purple. */
export function colorFromTriplet(triplet: string | null | undefined): RGB {
  const parts = (triplet ?? "").trim().split(/\s+/).map(Number);
  if (parts.length === 3 && parts.every((n) => Number.isFinite(n) && n >= 0 && n <= 255)) {
    return rgb(parts[0] / 255, parts[1] / 255, parts[2] / 255);
  }
  return rgb(124 / 255, 58 / 255, 237 / 255);
}

const tint = (c: RGB, amount: number): RGB =>
  rgb(1 - (1 - c.red) * amount, 1 - (1 - c.green) * amount, 1 - (1 - c.blue) * amount);
const shade = (c: RGB, amount: number): RGB =>
  rgb(c.red * amount, c.green * amount, c.blue * amount);

const INK = rgb(0.09, 0.09, 0.11);
const MUTED = rgb(0.45, 0.45, 0.5);
const RULE = rgb(0.88, 0.88, 0.9);
const MARGIN = 48;
const RIGHT = A4.width - MARGIN;
const CONTENT = RIGHT - MARGIN;
const ROW = 18;

export interface InvoicePdfOptions {
  payout: BrandPayoutHistory;
  brandName: string | null;
  /** Brand colour as an "r g b" token triplet; used for the avatar and the Paid pill. */
  brandColor?: string | null;
}

export async function buildPayoutInvoicePdf({
  payout,
  brandName,
  brandColor,
}: InvoicePdfOptions): Promise<Uint8Array> {
  const { doc, font, bold } = await createDocument();
  const page = doc.addPage([A4.width, A4.height]);
  const brand = colorFromTriplet(brandColor);

  const isPaid = payout.status === "PAID";
  const paidOn = payout.paidAt ?? payout.updatedAt;
  const affiliateName =
    `${payout.affiliate.firstName ?? ""} ${payout.affiliate.lastName ?? ""}`.trim();

  // A label on the left and a value on the right, shrinking the value's font
  // if a long one (an email, a wallet address) would otherwise collide.
  const row = (y: number, label: string, value: string, strong = false) => {
    const f: PDFFont = strong ? bold : font;
    const labelWidth = drawText(page, f, label, {
      x: MARGIN,
      y,
      size: strong ? 11 : 10,
      color: strong ? INK : MUTED,
    });
    const room = CONTENT - labelWidth - 16;
    let size = strong ? 11 : 10;
    const natural = f.widthOfTextAtSize(value, size);
    if (natural > room) size = Math.max(7, (size * room) / natural);
    drawText(page, f, value, { x: RIGHT, y, size, align: "right", color: INK });
  };
  const rule = (y: number) =>
    page.drawLine({
      start: { x: MARGIN, y: A4.height - y },
      end: { x: RIGHT, y: A4.height - y },
      thickness: 0.6,
      color: RULE,
    });

  // ── Header: issuer, title, status pill ─────────────────────────────────
  let y = 60;
  if (brandName) {
    drawText(page, font, brandName, { x: MARGIN, y, size: 10, color: MUTED });
    y += 18;
  }
  drawText(page, bold, "Payout invoice", { x: MARGIN, y, size: 16, color: INK });

  {
    const label = isPaid ? "Paid" : "Pending";
    const size = 9;
    const textWidth = bold.widthOfTextAtSize(label, size);
    const h = 18;
    const w = textWidth + 20;
    const top = y - 13; // pill vertically centred on the title's cap height
    const fill = isPaid ? tint(brand, 0.14) : rgb(0.93, 0.93, 0.94);
    const ink = isPaid ? shade(brand, 0.85) : MUTED;
    const cy = A4.height - (top + h / 2);
    page.drawCircle({ x: RIGHT - w + h / 2, y: cy, size: h / 2, color: fill });
    page.drawCircle({ x: RIGHT - h / 2, y: cy, size: h / 2, color: fill });
    page.drawRectangle({ x: RIGHT - w + h / 2, y: cy - h / 2, width: w - h, height: h, color: fill });
    // Cap height of Helvetica ≈ 0.72em; centre the caps on the pill's midline.
    drawText(page, bold, label, {
      x: RIGHT - w / 2,
      y: top + h / 2 + size * 0.36,
      size,
      align: "center",
      color: ink,
    });
  }

  // ── The one figure that matters ────────────────────────────────────────
  y += 36;
  drawText(page, font, isPaid ? "Amount paid" : "Amount requested", {
    x: MARGIN,
    y,
    size: 10,
    color: MUTED,
  });
  y += 28;
  drawText(page, bold, formatCurrency(payout.amount), { x: MARGIN, y, size: 26, color: INK });
  y += 18;
  drawText(
    page,
    font,
    isPaid
      ? `Paid on ${formatInvoiceDate(paidOn)}`
      : `Requested on ${formatInvoiceDate(payout.createdAt)}, awaiting payment`,
    { x: MARGIN, y, size: 10, color: MUTED }
  );

  // ── Recipient ──────────────────────────────────────────────────────────
  y += 22;
  rule(y);
  y += 24;
  drawText(page, font, isPaid ? "Paid to" : "Payable to", { x: MARGIN, y, size: 10, color: MUTED });
  y += 12;
  const radius = 18;
  const cx = MARGIN + radius;
  const cy = y + radius;
  const photo =
    payout.affiliate.profileImage && !isGeneratedAvatar(payout.affiliate.profileImage)
      ? await embedImageFromUrl(doc, payout.affiliate.profileImage)
      : null;
  if (photo) {
    drawCircularImage(page, photo, { cx, cy, radius });
  } else {
    page.drawCircle({ x: cx, y: A4.height - cy, size: radius, color: brand });
    drawText(page, bold, personInitials(affiliateName), {
      x: cx,
      y: cy + 12 * 0.36,
      size: 12,
      align: "center",
      color: rgb(1, 1, 1),
    });
  }
  drawText(page, bold, affiliateName || "—", { x: cx + radius + 12, y: cy - 2, size: 11, color: INK });
  drawText(page, font, payout.affiliate.email ?? "", {
    x: cx + radius + 12,
    y: cy + 13,
    size: 10,
    color: MUTED,
  });
  y = cy + radius;

  // ── Reference facts, each stated once ──────────────────────────────────
  y += 16;
  rule(y);
  y += 24;
  row(y, "Invoice number", payout.id);
  y += ROW;
  row(y, "Requested on", formatInvoiceDate(payout.createdAt));
  if (isPaid) {
    y += ROW;
    row(y, "Paid on", formatInvoiceDate(paidOn));
  }
  if (payout.referenceNumber) {
    y += ROW;
    row(y, "Payment reference", payout.referenceNumber);
  }
  if (payout.code) {
    y += ROW;
    row(y, "Affiliate code", payout.code);
  }

  // ── Where the money went (or can go) ───────────────────────────────────
  y += 12;
  rule(y);
  y += 24;
  const methods = paymentMethodsOf(payout);
  if (methods.length === 0) {
    row(y, "Payment method", "—");
  } else {
    methods.forEach((method, index) => {
      if (index > 0) y += ROW;
      row(y, methods.length > 1 ? `Payment method ${index + 1}` : "Payment method", method.name);
      for (const [key, value] of Object.entries(method.fields)) {
        y += ROW;
        row(y, fieldLabel(key), String(value));
      }
    });
  }

  // ── Amounts ────────────────────────────────────────────────────────────
  y += 12;
  rule(y);
  y += 24;
  const breakdown = payout.breakdown;
  if (breakdown && breakdown.recurringAmount > 0) {
    row(y, "One-time commissions", formatCurrency(breakdown.oneTimeAmount));
    y += ROW;
    const renewals = breakdown.renewalCount;
    row(
      y,
      renewals > 0
        ? `Recurring commissions (${renewals} ${renewals === 1 ? "renewal" : "renewals"})`
        : "Recurring commissions",
      formatCurrency(breakdown.recurringAmount)
    );
    y += ROW;
  }
  row(y, "Processing fee", formatCurrency(0));
  y += 10;
  rule(y);
  y += 20;
  row(y, isPaid ? "Total paid" : "Total due", formatCurrency(payout.amount), true);

  // ── Footer ─────────────────────────────────────────────────────────────
  drawText(
    page,
    font,
    `Electronic invoice generated on ${format(new Date(), "MMMM d, yyyy")}.`,
    { x: MARGIN, y: A4.height - 40, size: 8, color: MUTED }
  );

  return doc.save();
}
